import { BoundingBox } from './model/bounding-box';
import { View } from './model/view';
import { Coordinate } from './model/coordinate';
import { Planarea, RenderMode } from './model/planarea';
import { EditMode } from './model/edit-mode';
import { Measurement, MeasurementVisualStyle } from './model/measurement';
import { EditState } from './edit-state/edit-state';

export class Renderer {
  view = new View();
  planareas = [];
  measurements = [];
  eventTokens = {};
  documentEventTokens = {};
  maxPlanareaId = 0;

  constructor(options) {
    ({
      canvasSelector: this.canvasSelector = '',
      isFullScreen: this.isFullScreen = false,
      onBorderActivated: this.onBorderActivated,
      onPlanareaActivated: this.onPlanareaActivated,
      onBaseBorderChanged: this.onBaseBorderChanged,
      onPlanareaSplitted: this.onPlanareaSplitted,
      onAfterInit: this.onAfterInit,
      additionalOptions: this.additionalOptions = {},
      editState: this.editState,
      editMode: this.editMode,
      translator: this.translator,
      formatPatternScale: this.formatPatternScale,
    } = options);

    this.canvasElement = document.querySelector(this.canvasSelector);
    this.view.zoomMultiplier = options.zoomMultiplier || 1.2;
    this.init();
  }

  init() {
    dojo.require('dojo.html');
    dojo.require('dojox.gfx');
    dojo.require('dojox.gfx.Moveable');
    dojo.addOnLoad(() => {
      this.prepareCanvas();
      this.initPlanareas(this.additionalOptions);

      if (this.editMode === EditMode.SHAPEONLY) {
        this.fitPlanToView();
        this.redraw();
      } else {
        // activate first planarea as default
        const defaultActivePlanarea = this.planareas[0];
        defaultActivePlanarea.activate();
        this.onPlanareaActivated(defaultActivePlanarea);
      }

      if (this.onAfterInit) this.onAfterInit();
    });
  }

  prepareCanvas() {
    this.surface = dojox.gfx.createSurface(this.canvasElement);
    this.surface.setDimensions(this.canvasElement.offsetWidth, this.canvasElement.offsetHeight);
    this._resetGroups();
  }

  _resetGroups() {
    this.group ? this.group.clear() : (this.group = this.surface.createGroup());
    this.planareasGroup = this.group.createGroup();
    this.measurementGroup = this.group.createGroup();
    this.splitMeasurementGroup = this.group.createGroup();
    this.trimLabelGroup = this.group.createGroup();
  }

  initPlanareas() {
    this.measurements = [];
    this.planareas = [];
    this.boundingBox = null;
    this.editState.areas.forEach(areaState => {
      const planarea = new Planarea({
        areaState: areaState,
        group: this.planareasGroup,
        view: this.view,
        renderMode: this.editMode === EditMode.SHAPEONLY ? RenderMode.ShapeOnly : RenderMode.AreaEdit,
        additionalOptions: { bordersSelectable: this.additionalOptions.bordersSelectable },
        onPlanareaActivated: this.planareaActivated.bind(this),
        onBorderActivated: this.borderActivated.bind(this),
        onBaseBorderChanged: this.baseBorderChanged.bind(this),
        onPlanareaSplitted: this.onPlanareaSplitted,
        onSplitViewChanged: this.splitViewChanged.bind(this),
        translator: this.translator,
      });
      const boundingRect = planarea.originalTessellation.poly.getBoundingRect();
      const boundingBox = new BoundingBox(
        new Coordinate(boundingRect[1].x, boundingRect[1].y),
        new Coordinate(boundingRect[0].x, boundingRect[0].y)
      );

      this.planareas.push(planarea);

      this.boundingBox ? this.boundingBox.combineWith(boundingBox) : (this.boundingBox = boundingBox);

      this.measurements.push(
        new Measurement({
          parentGroup: this.measurementGroup,
          planarea: planarea,
          canvas: this.canvasElement,
          view: this.view,
          formatPatternScale: this.formatPatternScale,
        })
      );
    });
  }

  updatePlanareas() {
    this.planareas.forEach(planarea => {
      planarea.updatePlanarea();
    });
  }

  refresh() {
    this._resetGroups();
    this.initPlanareas();
  }

  redraw(visualOptions) {
    this.splitMeasurementGroup.clear();
    this.splitMeasurement = null;
    if (this.editMode === EditMode.SHAPEONLY) {
      this.trimLabelGroup.clear();
      this.planareas.forEach(planarea => {
        planarea.renderTrimLabels(this.trimLabelGroup, 10);
      });
    } else {
      this.measurements.forEach(measurement => {
        measurement.render();
      });
    }

    let activePlanarea;
    this.planareas.forEach(planarea => {
      planarea.render(visualOptions);
      if (planarea.activated) {
        activePlanarea = planarea;
      }
    });

    if (activePlanarea) {
      activePlanarea.group.moveToFront();
    }
  }

  fitPlanToView() {
    this._updateViewOffset();
    const padding = this.editMode === EditMode.SHAPEONLY ? 40 : 100;
    const containerPosition = {
      w: this.canvasElement.offsetWidth,
      h: this.canvasElement.getAttribute('data-max-height'),
    };

    const rzoom = Math.max(
      (this.boundingBox.max.x - this.boundingBox.min.x) / (containerPosition.w - padding),
      (this.boundingBox.max.y - this.boundingBox.min.y) / (containerPosition.h - padding)
    );

    this.view.zoomLevel = -1 * Math.ceil(Math.log(rzoom) / Math.log(this.view.zoomMultiplier));

    const pzoom = Math.pow(this.view.zoomMultiplier, this.view.zoomLevel);
    const newHeight = Math.min(
      (this.boundingBox.max.y - this.boundingBox.min.y) * pzoom + padding + 48,
      containerPosition.h
    );

    this.canvasElement.style.height = newHeight + 'px';

    this.view.pan.x =
      Math.floor(0.5 * (containerPosition.w - (this.boundingBox.max.x - this.boundingBox.min.x) * pzoom)) -
      this.boundingBox.min.x * pzoom;
    this.view.pan.y =
      Math.floor(0.5 * (containerPosition.h - (this.boundingBox.max.y - this.boundingBox.min.y) * pzoom)) -
      this.boundingBox.min.y * pzoom -
      (containerPosition.h - newHeight) * 0.5;
    this.view.zoomPan.x = 0;
    this.view.zoomPan.y = 0;

    this.surface.setDimensions(this.canvasElement.offsetWidth, this.canvasElement.offsetHeight);
  }

  zoomIn(zoomPoint) {
    const surfaceZoomPoint = zoomPoint || this._getCanvasCenter();

    this.view.zoomIn(surfaceZoomPoint);

    this.redraw();
  }

  zoomOut(zoomPoint) {
    const surfaceZoomPoint = zoomPoint || this._getCanvasCenter();

    this.view.zoomOut(surfaceZoomPoint);

    this.redraw();
  }

  _getCanvasCenter() {
    return new Coordinate(
      parseFloat(this.canvasElement.offsetWidth) / 2.0,
      parseFloat(this.canvasElement.offsetHeight) / 2.0
    );
  }

  switchEditMode(mode) {
    this._resetEventTokens();

    switch (mode) {
      case EditMode.POINTER:
        break;
      case EditMode.MOVE:
        this._applyMoveMode();
        break;
    }
  }

  planareaActivated(planarea) {
    this.planareas.forEach(planarea => {
      planarea.deactivate();
    });
    this.onPlanareaActivated(planarea);
    planarea.activate();
    this.redraw();
  }

  borderActivated(border) {
    this.onBorderActivated(border);
  }

  baseBorderChanged(border) {
    this.onBaseBorderChanged(border);
  }

  _applyMoveMode() {
    let oldPosition = null;
    let isMoving = false;

    this.eventTokens['editModeMoveStart'] = this.surface.connect('mousedown', e => {
      e.preventDefault();
      e.stopPropagation();

      if (!isMoving) {
        isMoving = true;
        this.eventTokens['editModeMoveMoving'] = this.surface.connect('mousemove', e => {
          let currentPosition = new Coordinate(e.pageX, e.pageY);
          if (!oldPosition) {
            oldPosition = currentPosition;
          } else {
            this.view.move(oldPosition, currentPosition);
            oldPosition = currentPosition;
            this.redraw();
          }
        });
      }
    });

    this.documentEventTokens['editModeMoveEnd'] = e => {
      oldPosition = null;
      if (this.eventTokens['editModeMoveMoving']) {
        this.surface.disconnect(this.eventTokens['editModeMoveMoving']);
        isMoving = false;
      }
    };

    document.addEventListener('mouseup', this.documentEventTokens['editModeMoveEnd']);
  }

  splitViewChanged(measurementPoints) {
    if (this.splitMeasurement) {
      this.splitMeasurement.setNewPoints(measurementPoints.points, measurementPoints.additionalPoints);
      this.splitMeasurement.render();
    } else {
      this.splitMeasurement = new Measurement({
        parentGroup: this.splitMeasurementGroup,
        canvas: this.canvasElement,
        view: this.view,
        points: measurementPoints.points,
        additionalPoints: measurementPoints.additionalPoints,
        offset: 30,
        style: MeasurementVisualStyle.splitting,
        formatPatternScale: this.formatPatternScale,
      });
    }
  }

  _resetEventTokens() {
    if (this.eventTokens['editModeMoveMoving'] !== undefined) {
      this.surface.disconnect(this.eventTokens['editModeMoveMoving']);
    }
    if (this.eventTokens['editModeMoveStart'] !== undefined) {
      this.surface.disconnect(this.eventTokens['editModeMoveStart']);
    }
    if (this.documentEventTokens['editModeMoveEnd'] !== undefined) {
      document.removeEventListener('mouseup', this.documentEventTokens['editModeMoveEnd']);
    }
  }

  activatePlanareaById(id) {
    for (const planarea of this.planareas) {
      if (planarea.id === id) {
        planarea.activate();
        this.onPlanareaActivated(planarea);
        return;
      }
    }
  }

  _updateViewOffset() {
    const position = dojo.position(this.canvasElement, true);
    this.view.pageCanvasOffset = new Coordinate(position.x, position.y);
  }
}

export const VisualOptions = Object.freeze({
  AnimateBoards: Symbol('animateBoards'),
});
